import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ServiceType } from '@common/enums';
import { BackupHistoryItem, HistoryPeriods, HistoryUnits, PieDataItem } from '@common/models';
import { ODataService } from '@common/odata';
import { SharedOdataService } from '@common/services';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class HistoryService extends SharedOdataService<any> {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);
  private readonly http = inject(HttpClient);

  protected readonly authUserId = this.authContext.id;
  protected readonly authDomainId = this.authContext.domainId;

  protected readonly isAdmin = this.ability.isAdmin;

  constructor() {
    super('BackupUsers');
  }

  usageHistory(period: HistoryPeriods, units: HistoryUnits): Observable<BackupHistoryItem[]> {
    const isAdmin = this.isAdmin();
    const id = isAdmin ? this.authDomainId() : this.authUserId();
    const serviceName = isAdmin ? 'Domains' : 'BackupUsers';
    const newOData: ODataService<BackupHistoryItem> = this.odataFactory.CreateService<BackupHistoryItem>(
      `/${serviceName}(${id})/UsageHistory`
    );

    return newOData
      .Query()
      .CustomQueryOptions([
        { key: 'period', value: period },
        { key: 'units', value: units }
      ])
      .Exec();
  }

  userUsageHistory(id: string, period: HistoryPeriods, units: HistoryUnits): Observable<BackupHistoryItem[]> {
    const newOData = this.odataFactory.CreateService<BackupHistoryItem>(`/BackupUsers(${id})/UsageHistory`);
    return newOData
      .Query()
      .CustomQueryOptions([
        { key: 'period', value: period },
        { key: 'units', value: units }
      ])
      .Exec();
  }

  userUsageDetails(id: string, type: ServiceType): Observable<PieDataItem[]> {
    const newOData = this.odataFactory.CreateService<PieDataItem>(`/BackupUsers(${id})/UsageDetails`);
    const url = newOData
      .Query()
      .CustomQueryOptions([{ key: 'service', value: type }])
      .GetUrl();
    return this.http.get<{ Items: PieDataItem[]; Service: ServiceType }>(url).pipe(map((x) => x.Items));
  }

  domainUsageDetails(id: string, type: ServiceType): Observable<PieDataItem[]> {
    const newOData = this.odataFactory.CreateService<PieDataItem>(`/Domains(${id})/UsageDetails`);
    const url = newOData
      .Query()
      .CustomQueryOptions([{ key: 'service', value: type }])
      .GetUrl();
    return this.http.get<{ Items: PieDataItem[]; Service: ServiceType }>(url).pipe(map((x) => x.Items));
  }

  domainUsageHistory(id: string, period: HistoryPeriods, units: HistoryUnits): Observable<BackupHistoryItem[]> {
    const newOData = this.odataFactory.CreateService<BackupHistoryItem>(`/Domains(${id})/UsageHistory`);
    return newOData
      .Query()
      .CustomQueryOptions([
        { key: 'period', value: period },
        { key: 'units', value: units }
      ])
      .Exec();
  }
}
